import * as React from "react";
import { useEffect } from "react";
import {
  CalendarEventDetails,
  PlayerAccount,
  Post,
  Prompt,
  SOCIAL_NETWORK_POST_STATUS_NAMES,
  socialIcons,
  socialNetworkNames,
  SocialNetworkPosted,
  SocialNetworkPostStatus,
  SocialNetworksId,
} from "@/interfaces";
import "./styles.scss";
import { Checkbox, Dimmer, Dropdown, DropdownItemProps, Loader, Table, TableHeaderCellProps } from "semantic-ui-react";
import { PostBody } from "./PostBody";
import { useSocialCoachSession } from "@/utils";

type Props = {
  isLoading: boolean;
  eventDetails: CalendarEventDetails | undefined;
  onSelectedPostsChange: (posts: Post[]) => void;
  handlePost: (post: Post, socialPost?: SocialNetworkPosted) => void;
};

export type TableRecord = {
  isSelected: boolean;
  post: Post;
  player: PlayerAccount;
  prompt: Prompt | undefined;
  socialNetworkPosts: SocialNetworkPosted[];
};

export const PostsTable: React.FC<Props> = ({ isLoading, eventDetails, onSelectedPostsChange, handlePost }) => {
  const [tableRecords, setTableRecords] = React.useState<TableRecord[]>([]);

  const [playerOptions, setPlayerOptions] = React.useState<DropdownItemProps[]>([]);
  const [promptOptions, setPromptOptions] = React.useState<DropdownItemProps[]>([]);
  const [socialNetworkIdOptions, setSocialNetworkIdOptions] = React.useState<DropdownItemProps[]>([]);
  const [statusOptions, setStatusOptions] = React.useState<DropdownItemProps[]>([]);

  const [selectedPlayers, setSelectedPlayers] = React.useState<number[]>([]);
  const [selectedPrompts, setSelectedPrompts] = React.useState<number[]>([]);
  const [selectedSocialNetworkIds, setSelectedSocialNetworkIds] = React.useState<SocialNetworksId[]>([]);
  const [selectedStatuses, setSelectedStatuses] = React.useState<SocialNetworkPostStatus[]>([]);
  const [selectedPosts, setSelectedPosts] = React.useState<number[]>([]);
  const { isPlayer } = useSocialCoachSession();
  const selectableTableRecords = React.useMemo(
    () => tableRecords.filter(record => record.post.canBeUpdatedOrDeleted),
    [tableRecords]
  );
  const visibleSelectedPosts = React.useMemo(
    () => selectableTableRecords.filter(record => record.isSelected).map(record => record.post),
    [selectableTableRecords]
  );

  useEffect(() => {
    onSelectedPostsChange(visibleSelectedPosts);
  }, [visibleSelectedPosts]);

  useEffect(() => {
    setSelectedPlayers([]);
    setSelectedPrompts([]);
    setSelectedSocialNetworkIds([]);
    setSelectedStatuses([]);
    setSelectedPosts([]);
    if (!eventDetails) return;

    const socialNetworks = eventDetails.posts.flatMap(post => post.socialNetworks);

    const socialNetworkIds = [...new Set(socialNetworks.map(sn => sn.socialNetworkId))];
    setSocialNetworkIdOptions(
      socialNetworkIds.map(sn => ({
        key: sn,
        text: socialNetworkNames[sn],
        value: sn,
        image: {
          src: socialIcons[sn],
          title: socialNetworkNames[sn],
          className: "socialNetworkIconOption",
          circular: true,
        },
      }))
    );

    const statuses = [...new Set(socialNetworks.map(sn => sn.status))];
    setStatusOptions(statuses.map(s => ({ key: s, text: SOCIAL_NETWORK_POST_STATUS_NAMES[s], value: s })));

    const playerOptions = eventDetails.players
      .sort((a, b) => ((a.email || "") > (b.email || "") ? 1 : (a.email || "") < (b.email || "") ? -1 : 0))
      .map(p => ({ key: p.playerId, text: p.email, value: p.playerId }));
    setPlayerOptions(playerOptions);

    const promptOptions = eventDetails.prompts
      .sort((a, b) => ((a.title || "") > (b.title || "") ? 1 : (a.title || "") < (b.title || "") ? -1 : 0))
      .map(p => ({ key: p.promptId, text: p.title, value: p.promptId }));
    setPromptOptions(promptOptions);
  }, [eventDetails]);

  useEffect(() => {
    setTableRecords([]);
    if (!eventDetails) return;

    const tableData: TableRecord[] = eventDetails.posts.flatMap(post => {
      if (selectedPlayers.length > 0 && !selectedPlayers.includes(post.playerId)) return [];
      if (selectedPrompts.length > 0 && (!post.promptId || !selectedPrompts.includes(post.promptId))) return [];

      const player = eventDetails.players.find(p => p.playerId === post.playerId);
      if (!player) return [];
      const prompt = eventDetails.prompts.find(p => p.promptId === post.promptId);
      const socialNetworkPosts = post.socialNetworks.flatMap(socialNetworkPost => {
        if (
          selectedSocialNetworkIds.length > 0 &&
          !selectedSocialNetworkIds.includes(socialNetworkPost.socialNetworkId)
        )
          return [];
        if (selectedStatuses.length > 0 && !selectedStatuses.includes(socialNetworkPost.status)) return [];
        return [socialNetworkPost];
      });
      if (socialNetworkPosts.length === 0) return [];
      const isSelected = selectedPosts.some(postId => postId === post.postId);
      return [{ isSelected, post, player, prompt, socialNetworkPosts }];
    });
    setTableRecords(tableData);
  }, [eventDetails, selectedPlayers, selectedPrompts, selectedSocialNetworkIds, selectedStatuses, selectedPosts]);

  const getHeaderCellWithDropdown = (
    text: string,
    options: DropdownItemProps[],
    selectedOptions: any[],
    setOptions: React.Dispatch<React.SetStateAction<any[]>>,
    isCentered: boolean = false
  ) => {
    const headerCellProps: TableHeaderCellProps = {
      width: 1,
      textAlign: isCentered ? "center" : "left",
      rowSpan: 2,
    };
    if (options.length <= 1) return <Table.HeaderCell {...headerCellProps}>{text}</Table.HeaderCell>;

    const handleOptionSelected = (event: any, option: any) => {
      event.stopPropagation();
      if (selectedOptions.includes(option)) {
        setOptions(selectedOptions.filter(el => el !== option));
      } else {
        setOptions([...selectedOptions, option]);
      }
    };
    const effectiveText = selectedOptions.length > 0 ? `${text} (${selectedOptions.length})` : text;
    return (
      <Table.HeaderCell {...headerCellProps}>
        <Dropdown text={effectiveText} multiple icon="dropdown" className="headerDropdown">
          <Dropdown.Menu>
            <Dropdown.Menu scrolling>
              {options.map(option => (
                <Dropdown.Item
                  {...option}
                  active={selectedOptions.includes(option.value)}
                  onClick={event => handleOptionSelected(event, option.value)}
                />
              ))}
            </Dropdown.Menu>
          </Dropdown.Menu>
        </Dropdown>
      </Table.HeaderCell>
    );
  };

  const getHeaderCellWithCheckbox = () => {
    const selectableCount = selectableTableRecords.length;
    if (selectableCount === 0) return null;

    const selectedCount = selectableTableRecords.filter(record => record.isSelected).length;
    return (
      <Table.HeaderCell className="actionsColumn" textAlign="center" rowSpan={2}>
        <Checkbox
          indeterminate={selectedCount >= 1 && selectedCount < selectableCount}
          checked={selectedCount >= 1 && selectedCount === selectableCount}
          onChange={() => {
            const selectablePostIds = selectableTableRecords.map(record => record.post.postId);
            if (selectedCount === selectableCount) {
              setSelectedPosts(current => {
                return current.filter(postId => !selectablePostIds.includes(postId));
              });
            } else {
              setSelectedPosts(current => {
                return [...new Set([...current, ...selectablePostIds])];
              });
            }
          }}
        />
      </Table.HeaderCell>
    );
  };

  const getTableBodies = () => {
    if (isLoading || tableRecords.length === 0) {
      return (
        <Table.Body>
          <Table.Row>
            <Table.Cell colSpan={5 + (isPlayer ? 0 : 1) + (selectableTableRecords.length > 0 ? 1 : 0)}>
              <div className="tableZeroStateContainer">
                {isLoading && (
                  <Dimmer active inverted>
                    <Loader inverted>Loading</Loader>
                  </Dimmer>
                )}
                {!isLoading && <span className="noPostsLabel">No posts found</span>}
              </div>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      );
    } else {
      return tableRecords.map(record => (
        <PostBody
          key={record.post.postId}
          record={record}
          canSelect={selectableTableRecords.length > 0}
          setSelectedPosts={setSelectedPosts}
          handlePost={handlePost}
        />
      ));
    }
  };

  return (
    <Table className="postsTable" structured compact={"very"}>
      <Table.Header>
        <Table.Row>
          {getHeaderCellWithCheckbox()}
          {isPlayer ? <></> : getHeaderCellWithDropdown("Player", playerOptions, selectedPlayers, setSelectedPlayers)}
          {getHeaderCellWithDropdown(
            promptOptions.length > 0 ? "Prompt" : "",
            promptOptions,
            selectedPrompts,
            setSelectedPrompts,
            true
          )}
          {getHeaderCellWithDropdown(
            "Network",
            socialNetworkIdOptions,
            selectedSocialNetworkIds,
            setSelectedSocialNetworkIds,
            false
          )}
          {getHeaderCellWithDropdown("Status", statusOptions, selectedStatuses, setSelectedStatuses)}
          <Table.HeaderCell rowSpan={2}>Timestamp</Table.HeaderCell>
          <Table.HeaderCell colSpan={4} textAlign={"center"}>
            Stats
          </Table.HeaderCell>
        </Table.Row>
        <Table.Row>
          <Table.HeaderCell width={1}>Impressions</Table.HeaderCell>
          <Table.HeaderCell width={1}>Reactions</Table.HeaderCell>
          <Table.HeaderCell width={1}>Comments</Table.HeaderCell>
          <Table.HeaderCell width={1}>Shares</Table.HeaderCell>
        </Table.Row>
      </Table.Header>

      {getTableBodies()}
    </Table>
  );
};
